import {
  Component,
  model,
  output,
  ViewChild,
  inject,
  computed,
  OnInit,
  input,
} from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NgScrollbar } from 'ngx-scrollbar';
import { Store } from '@ngrx/store';
import { toSignal } from '@angular/core/rxjs-interop';
import { submittalPackageSidebarActions } from '../submittal-package-sidebar.actions';
import { SimpleButtonComponent } from '@app/framework/buttons/simple-medium-button/simple-button.component';
import { AddRemoveButtonsComponent } from '@app/framework/buttons/add-remove-buttons/add-remove-buttons.component';
import { NotificationsService } from '@app/services/notifications.service';
import { DropdownComponent } from '@app/framework/inputs/dropdown/dropdown.component';
import { submittalsSelectors } from '@app/store/submittals/submittals.selectors';
import { viewProjectSelectors } from '@app/store/view-project/view-project.selectors';
import {
  SubmittalPackageModel,
  SubmittalPackageModelRegistryItem,
} from '@app/store/submittals/submittals.types';
import { SidebarHeaderComponent } from '@app/framework/interaction-bar/sidebar-header/sidebar-header.component';

@Component({
  selector: 'app-submittal-registry-items',
  imports: [
    FormsModule,
    CommonModule,
    NgScrollbar,
    SimpleButtonComponent,
    AddRemoveButtonsComponent,
    DropdownComponent,
    SidebarHeaderComponent,
  ],
  templateUrl: './submittal-registry-items.component.html',
  styleUrl: './submittal-registry-items.component.scss',
})
export class SubmittalRegistryItemsComponent implements OnInit {
  private readonly notif = inject(NotificationsService);
  private readonly store = inject(Store);

  @ViewChild('registryItemsForm') registryItemsForm: NgForm;

  projectId = toSignal(this.store.select(viewProjectSelectors.selectProjectId));

  packageId = input<number | null>(null);
  model = model<SubmittalPackageModel>();
  savePackage = output<void>();
  goBackPage = output<void>();

  // Fetch actual registry data from backend
  allRegistryItems = toSignal(this.store.select(submittalsSelectors.selectAllSubmittalRegistries));
  registryItemsLoading = toSignal(
    this.store.select(submittalsSelectors.selectSubmittalRegistriesLoading),
  );

  registryItemOptions = computed(() => {
    const allRegistryItems = this.allRegistryItems();
    const packageId = this.packageId();
    const currentPackageItemIds = new Set(
      this.model()
        ?.registry_items?.map((item) => item.id)
        .filter((id) => id !== null) ?? [],
    );

    if (packageId) {
      // In edit mode: show entries without packages + entries already in this package
      const itemsWithoutPackageOrInCurrentPackage = allRegistryItems?.filter(
        (item) => !item.package || currentPackageItemIds.has(item.id),
      );
      return itemsWithoutPackageOrInCurrentPackage;
    } else {
      // in create mode, filter out items that have packages
      const itemsWithoutPackage = allRegistryItems?.filter((item) => !item.package);
      return itemsWithoutPackage;
    }
  });

  loadingMoreRegistryItems = toSignal(
    this.store.select(submittalsSelectors.selectSubmittalRegistriesLoading),
  );

  registryItemCount = computed(() => {
    return this.model()?.registry_items?.length || 0;
  });

  disabledRegistryItemIds = computed<Set<string | number>>(() => {
    const packageId = this.packageId();
    const allRegistryItems = this.allRegistryItems();
    const currentPackageItemIds = new Set(
      this.model()
        ?.registry_items?.map((item) => item.id)
        .filter((id) => id !== null) ?? [],
    );

    if (packageId) {
      // In edit mode: disable items that are already in the current package
      return currentPackageItemIds;
    } else {
      // In create mode: disable already selected items
      return currentPackageItemIds;
    }
  });

  ngOnInit() {
    const projectId = this.projectId();
    if (projectId) {
      this.store.dispatch(
        submittalPackageSidebarActions.submittalRegistryListInitialized({ projectId }),
      );
    }
  }

  isFormValid(): boolean {
    this.registryItemsForm.form.markAllAsTouched();

    const emptyItems = this.model()?.registry_items?.filter((item) => {
      return !item.id;
    });

    if (emptyItems && emptyItems.length > 0) {
      this.notif.showError('Please fill in all registry item names and descriptions');
      return false;
    }

    return this.registryItemsForm.form.valid;
  }

  addRegistryItem(index: number) {
    const newItem: SubmittalPackageModelRegistryItem = {
      id: null,
      spec_section: null,
      spec_description: null,
      item_description: null,
    };

    this.model.update((current) => {
      const items = [...(current?.registry_items || [])];
      items.splice(index + 1, 0, newItem);
      return {
        ...current,
        registry_items: items,
      };
    });
  }

  removeRegistryItem(index: number) {
    this.model.update((current) => {
      const items = [...(current?.registry_items || [])];
      items.splice(index, 1);
      return {
        ...current,
        registry_items: items,
      };
    });
  }

  onRegistryItemChange(registryId: string, index: number) {
    this.model.update((current) => {
      const registry_items = [...(current?.registry_items || [])];
      const entries = this.registryItemOptions() || [];
      const foundEntry = entries.find((entry) => entry.id === Number(registryId));
      if (foundEntry) {
        registry_items[index] = {
          id: foundEntry.id,
          spec_section: foundEntry.spec_section,
          spec_description: foundEntry.spec_description,
          item_description: foundEntry.item_description,
        };
      }
      return {
        ...current,
        registry_items,
      };
    });
  }

  loadMoreRegistryItemOptions() {
    const projectId = this.projectId();
    if (projectId) {
      this.store.dispatch(
        submittalPackageSidebarActions.submittalRegistryListEndReached({ projectId }),
      );
    }
  }

  removeAllRegistryItems() {
    this.model.update((current) => {
      return {
        ...current,
        registry_items: [],
      };
    });
    this.addRegistryItem(0);
  }

  submitForm() {
    if (!this.isFormValid()) {
      return;
    }

    this.savePackage.emit();
  }

  goBack() {
    this.goBackPage.emit();
  }
}
